import { isSameYear } from 'date-fns/isSameYear';
import React from 'react';

import { format as formatDate } from '@eika-infrastruktur/date';
import { formatAccountNumber } from '@eika-infrastruktur/format';
import { LinkButton } from '@eika/button';
import { Icon, IconTypes } from '@eika/icon';
import { AttentionLabel } from '@eika/label';

import { useAccountNumber } from '../../fetchHooks/useAccount';
import type { PaymentAgreementId, RecurringPaymentAgreement } from '../../generated-types';
import { useReceiver } from '../../hooks/useSetReceiver';
import useTranslation, { format } from '../../hooks/useTranslation';
import { paymentTrackIDs } from '../../utils/gtm/trackIDs/payment';
import { AccountLabels } from '../shared/AccountLabels';
import { currencyFormatter } from '../shared/numberFormat';

interface Props {
    recurringPayment: RecurringPaymentAgreement;
    onShow: (id: PaymentAgreementId) => void;
}

const DAY_MONTH_FORMAT = 'd. MMMM';
const DAY_MONTH_YEAR_FORMAT = 'd. MMMM yyyy';

const formatDateWithYearOrMonth = (date: string) => {
    const today = new Date();
    const targetDate = new Date(date);
    if (isSameYear(targetDate, today)) {
        return formatDate(targetDate, DAY_MONTH_FORMAT);
    }

    return formatDate(targetDate, DAY_MONTH_YEAR_FORMAT);
};

const RecurringPayment: React.FC<Props> = ({ recurringPayment, onShow }) => {
    const translated = useTranslation();
    const { amount, nextPaymentDate, paymentInterval, status, text, toAccount, currency } = recurringPayment;

    const receiver = useReceiver(toAccount);
    const { data: fromAccount } = useAccountNumber(recurringPayment.fromAccountNumber);

    return (
        <li className="recurring-payment">
            <div className="recurring-payment__status">
                <Icon type={IconTypes.CALENDAR_20} />
                {status === 'INACTIVE' && (
                    <AttentionLabel size="small" text={translated.recurringPaymentStatus[status]} />
                )}
                {recurringPayment.lastPaymentDate && (
                    <AttentionLabel
                        size="small"
                        text={format(
                            translated.recurringPayments.endDate,
                            formatDateWithYearOrMonth(recurringPayment.lastPaymentDate),
                        )}
                    />
                )}
                {fromAccount && (
                    <AccountLabels className="recurring-payment__owner-wrapper" owners={fromAccount.owners} />
                )}
            </div>
            <div className="recurring-payment__account-info">
                <dl>
                    <dt className="recurring-payment__data-label">{translated.from}</dt>
                    <dd className="recurring-payment__data recurring-payment__account-data">
                        {fromAccount && <span className="recurring-payment__account-alias">{fromAccount.alias}</span>}
                        <small className="recurring-payment__account-number">
                            {formatAccountNumber(recurringPayment.fromAccountNumber)}
                        </small>
                    </dd>
                </dl>
                <dl>
                    <dt className="recurring-payment__data-label">{translated.to}</dt>
                    <dd className="recurring-payment__data recurring-payment__account-data">
                        {receiver?.accountName ? (
                            <span className="recurring-payment__account-alias">{receiver.accountName} </span>
                        ) : (
                            <span className="recurring-payment__account-alias">
                                {formatAccountNumber(receiver?.accountNumber)}
                            </span>
                        )}
                        {!!receiver?.accountName && (
                            <small className="recurring-payment__account-number">
                                {formatAccountNumber(receiver?.accountNumber)}
                            </small>
                        )}
                    </dd>
                </dl>
            </div>

            {text && <p className="recurring-payment__payment-text">{text}</p>}

            <dl className="recurring-payment__meta">
                <dt className="recurring-payment__data-label">{translated.recurringPayments.nextDate}</dt>
                <dd className="recurring-payment__data">{formatDate(nextPaymentDate, 'd. MMMM')}</dd>
            </dl>
            <dl className="recurring-payment__meta">
                <dt className="recurring-payment__data-label">{translated.recurringPayments.frequency}</dt>
                <dd className="recurring-payment__data">
                    {translated.dateField.recurringPaymentInterval[paymentInterval]}
                </dd>
            </dl>
            <dl className="recurring-payment__meta">
                <dt className="recurring-payment__data-label">{translated.recurringPayments.amount}</dt>
                <dd className="recurring-payment__data">{currencyFormatter(amount, currency)} </dd>
            </dl>

            <LinkButton
                className="recurring-payment__edit-button"
                onClick={() => onShow(recurringPayment.id)}
                icon={IconTypes.PENN_24}
                data-testid="edit-recurring-payment-button"
                data-track-id={paymentTrackIDs.recurring.agreement}
                data-track-linkname={translated.recurringPayments.detailsButton}
            >
                {translated.recurringPayments.detailsButton}
            </LinkButton>
        </li>
    );
};

export default RecurringPayment;
